import {PathologyId, UserId} from '@legit-health/ui';
import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useSharedDataContext} from '@/shared/components/layout/LoggedInLayout/contexts/SharedDataContext';
import {EncounterId} from '@/shared/interfaces/Encounter';
import {ImageProtocolId} from '@/shared/interfaces/ImageProtocol';
import isHidradenitisSuppurativa from '@/shared/utils/isHidradenitisSuppurativa';
import isPsoriasis from '@/shared/utils/isPsoriasis';
import ApasiGlobalWizard from './wizards/ApasiGlobalWizard';
import HidradenitisSuppurativaWizard from './wizards/HidradenitisSuppurativaWizard';

/**
 * Result passed to `onComplete` after a wizard successfully submits.
 * The HS wizard posts to `/image-protocols` and returns an `imageProtocolId`;
 * the APASI (psoriasis) wizard posts to `/apasi-global` and returns an `encounterId`.
 * The shape is discriminated so consumers can decide whether to navigate to an
 * image-protocol detail page or stay put.
 */
export type ImageProtocolCaptureResult =
  | {imageProtocolId: ImageProtocolId}
  | {encounterId: EncounterId};

interface ImageProtocolCaptureContentProps {
  patientId: UserId;
  assignedPathologies: PathologyId[];
  onComplete: (result: ImageProtocolCaptureResult) => void;
}

/**
 * Selects the wizard to render based on the patient's assigned pathologies.
 */
export default function ImageProtocolCaptureContent({
  patientId,
  assignedPathologies,
  onComplete
}: ImageProtocolCaptureContentProps) {
  const {t} = useTranslation(['shared/imageProtocol']);
  const {pathologiesById} = useSharedDataContext();

  const hasHidradenitisSuppurativa = assignedPathologies.some(function (pathologyId) {
    const pathology = pathologiesById[pathologyId];
    return pathology ? isHidradenitisSuppurativa(pathology) : false;
  });

  if (hasHidradenitisSuppurativa) {
    return <HidradenitisSuppurativaWizard patientId={patientId} onComplete={onComplete} />;
  }

  const hasPsoriasis = assignedPathologies.some(function (pathologyId) {
    const pathology = pathologiesById[pathologyId];
    return pathology ? isPsoriasis(pathology) : false;
  });

  if (hasPsoriasis) {
    return <ApasiGlobalWizard patientId={patientId} onComplete={onComplete} />;
  }

  return (
    <Box sx={{p: 4, textAlign: 'center'}}>
      <Typography variant="h6" gutterBottom>
        {t('shared/imageProtocol:unsupported.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('shared/imageProtocol:unsupported.description')}
      </Typography>
    </Box>
  );
}
